import { Fragment, useState } from 'react';
import { formatInTimeZone } from 'date-fns-tz';
import {
  BANDERAS_MER, BANDERAS_MOD, GONDOLAS, etiquetaBandera,
} from '../../../domain/pg/catalogo';
import { participacionPg } from '../../../domain/pg/gondola';
import { contradiccionesDeFicha, resumenDeFicha } from '../../../domain/pg/mercados';
import type { CierreMercado, FichaMercado, FichaModerno } from '../../../domain/pg/tipos';
import { fueraDeLaMaestra, nombreDeTienda } from '../../../domain/pg/tipos';
import { claveTexto, STOPWORDS_LUGAR, type GrupoMercado } from '../../../domain/pg/normalizar';
import { BotonDetalle, FILA_ABIERTA, PanelDeDetalle, Senal } from '../../../components/DetalleExpandible';
import DetalleMercado from './DetalleMercado';
import DetalleModerno from './DetalleModerno';
import type { Instrumento } from '../LevantamientoPgBandeja';

const TZ = 'America/Lima';
const hora = (iso: string | null): string =>
  (iso === null ? '' : formatInTimeZone(new Date(iso), TZ, 'HH:mm'));

const SIN = <span className="text-gray-400">—</span>;

/**
 * La lista de fichas, una por fila, la más reciente primero.
 *
 * Lo que se ve PLEGADO tiene que alcanzar para reconocer la ficha sin abrirla —día, dónde, quién,
 * si se midió y cuánto—, porque si no, todo el mundo abre todo y el detalle deja de servir de
 * detalle.
 *
 * ⚠️ La fecha se pinta **como cadena**, sin pasar por `new Date`. `fecha` es un `date` de Postgres
 * y llega como `2026-09-12`; construir un `Date` con eso lo interpreta en UTC y en Lima (UTC-5) la
 * pantalla mostraría el día anterior. Es un error que no se ve hasta que alguien cruza el reporte
 * con el Excel del cliente.
 */
export default function RegistrosPg({
  instrumento, mercados, moderno, cierres, grupoDeClave, cargando, hayFiltro, onLimpiar,
}: {
  instrumento: Instrumento;
  mercados: readonly FichaMercado[];
  moderno: readonly FichaModerno[];
  cierres: readonly CierreMercado[];
  grupoDeClave: Map<string, GrupoMercado>;
  cargando: boolean;
  hayFiltro: boolean;
  onLimpiar: () => void;
}) {
  const [abierta, setAbierta] = useState<string | null>(null);
  const total = instrumento === 'mercados' ? mercados.length : moderno.length;

  if (!cargando && total === 0) {
    return (
      <p className="mt-3 text-sm text-gray-600">
        {hayFiltro ? (
          <>
            El filtro dejó todas las fichas afuera.{' '}
            <button type="button" className="text-blue-700 underline" onClick={onLimpiar}>
              Limpiar el filtro
            </button>
          </>
        ) : (
          'No hay fichas de este instrumento entre esas fechas.'
        )}
      </p>
    );
  }

  return (
    <div className="mt-3 overflow-x-auto rounded border border-gray-200 bg-white">
      <table className="min-w-full text-sm">
        {instrumento === 'mercados' ? (
          <>
            <thead className="bg-gray-50 text-left text-xs uppercase text-gray-600">
              <tr>
                <th className="px-3 py-2">Fecha</th>
                <th className="px-3 py-2">Mercado</th>
                <th className="px-3 py-2">Puesto</th>
                <th className="px-3 py-2">Supervisor</th>
                <th className="px-3 py-2">Estado</th>
                <th className="px-3 py-2 text-right">Qué se midió</th>
                <th className="px-3 py-2">Señales</th>
                <th className="px-3 py-2"><span className="sr-only">Acciones</span></th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {mercados.map((f) => {
                const abierto = abierta === f.id;
                const g = grupoDeClave.get(claveTexto(f.mercadoNombre, STOPWORDS_LUGAR) ?? '');
                const r = resumenDeFicha(f);
                const contras = contradiccionesDeFicha(f);
                return (
                  <Fragment key={f.id}>
                    <tr className={`align-top ${abierto ? FILA_ABIERTA : ''}`}>
                      <td className="whitespace-nowrap px-3 py-2">
                        {f.fecha}
                        <div className="text-xs text-gray-500">{hora(f.ocurridoEn)}</div>
                      </td>
                      <td className="px-3 py-2">
                        {g?.rotulo ?? f.mercadoNombre ?? SIN}
                        {/* La grafía cruda va abajo cuando difiere del rótulo: es lo que deja
                            comprobar que la normalización agrupó lo que había que agrupar. */}
                        {g !== undefined && f.mercadoNombre !== null && g.rotulo !== f.mercadoNombre && (
                          <div className="text-xs text-gray-500">escrito «{f.mercadoNombre}»</div>
                        )}
                        <div className="mt-0.5 flex flex-wrap gap-1">
                          {g?.sinNormalizar === true && <Senal>sin normalizar</Senal>}
                          {g?.rotuloNoCoincideConGps === true && (
                            <Senal tono="grave">el nombre elegido no coincide con el GPS</Senal>
                          )}
                          {g?.mismoNombreDosSitios === true && (
                            <Senal tono="grave">mismo nombre, dos sitios</Senal>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        {f.puestoNumero === null ? SIN : <b>#{f.puestoNumero}</b>}
                        {f.puestoNombre !== null && (
                          <div className="text-xs text-gray-500">{f.puestoNombre}</div>
                        )}
                      </td>
                      <td className="px-3 py-2">{f.supervisor ?? SIN}</td>
                      <td className="px-3 py-2">
                        {f.levantado
                          ? <span className="text-gray-700">Levantado</span>
                          : (
                            <Senal>
                              No se pudo · {f.motivoNo ?? 'sin motivo'}
                            </Senal>
                          )}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {f.levantado ? (
                          <span className="text-xs text-gray-700">
                            {r.cats} cat · {r.marcas} marcas · {r.precios} precios
                          </span>
                        ) : SIN}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap gap-1">
                          {f.flags.map((x) => {
                            const e = etiquetaBandera(x, BANDERAS_MER);
                            return <Senal key={x} tono={e.tono}>{e.label}</Senal>;
                          })}
                          {contras.filter((c) => c.tono === 'grave').map((c) => (
                            <Senal key={c.clave} tono="grave">revisar</Senal>
                          ))}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <BotonDetalle abierto={abierto}
                          onClick={() => setAbierta(abierto ? null : f.id)} />
                      </td>
                    </tr>
                    {abierto && (
                      <tr>
                        <td colSpan={8} className="p-0">
                          <PanelDeDetalle
                            titulo="Ficha del puesto"
                            sujeto={`${g?.rotulo ?? f.mercadoNombre ?? 'sin mercado'} · puesto ${f.puestoNumero ?? '—'} · ${f.fecha}`}
                            onCerrar={() => setAbierta(null)}
                          >
                            <DetalleMercado ficha={f} cierres={cierres} grupo={g ?? null} />
                          </PanelDeDetalle>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </>
        ) : (
          <>
            <thead className="bg-gray-50 text-left text-xs uppercase text-gray-600">
              <tr>
                <th className="px-3 py-2">Fecha</th>
                <th className="px-3 py-2">Tienda</th>
                <th className="px-3 py-2">Cadena</th>
                <th className="px-3 py-2">Supervisor</th>
                <th className="px-3 py-2">Góndola · P&amp;G</th>
                <th className="px-3 py-2 text-right">Precios</th>
                <th className="px-3 py-2">Señales</th>
                <th className="px-3 py-2"><span className="sr-only">Acciones</span></th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {moderno.map((f) => {
                const abierto = abierta === f.id;
                const gs = f.ficha.gondolas ?? {};
                const precios = Object.values(gs)
                  .reduce((s, g) => s + (g.precios ?? []).length, 0);
                return (
                  <Fragment key={f.id}>
                    <tr className={`align-top ${abierto ? FILA_ABIERTA : ''}`}>
                      <td className="whitespace-nowrap px-3 py-2">
                        {f.fecha}
                        <div className="text-xs text-gray-500">{hora(f.ocurridoEn)}</div>
                      </td>
                      <td className="px-3 py-2">
                        {nombreDeTienda(f)}
                        {/* `pdv_id` en NULL NO es «fuera de catálogo»: 19 de 24 lo tienen y solo 3
                            se tecleraon. El criterio es la bandera del formulario. */}
                        {fueraDeLaMaestra(f) && (
                          <div className="mt-0.5"><Senal>escrita a mano</Senal></div>
                        )}
                      </td>
                      <td className="px-3 py-2">{f.cadena ?? SIN}</td>
                      <td className="px-3 py-2">{f.supervisor ?? SIN}</td>
                      <td className="px-3 py-2">
                        <div className="space-y-0.5 text-xs">
                          {GONDOLAS.map((g) => {
                            const p = participacionPg(g.id, gs[g.id]?.espacio);
                            return (
                              <div key={g.id}>
                                <span className="text-gray-500">{g.nom}: </span>
                                {p.medida
                                  ? <b className="tabular-nums">{p.pct} %</b>
                                  : <span className="text-gray-400">sin espacio medido</span>}
                              </div>
                            );
                          })}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {precios > 0 ? precios : SIN}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap gap-1">
                          {f.flags.map((x) => {
                            const e = etiquetaBandera(x, BANDERAS_MOD);
                            return <Senal key={x} tono={e.tono}>{e.label}</Senal>;
                          })}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <BotonDetalle abierto={abierto}
                          onClick={() => setAbierta(abierto ? null : f.id)} />
                      </td>
                    </tr>
                    {abierto && (
                      <tr>
                        <td colSpan={8} className="p-0">
                          <PanelDeDetalle
                            titulo="Ficha de la tienda"
                            sujeto={`${nombreDeTienda(f)} · ${f.fecha}`}
                            onCerrar={() => setAbierta(null)}
                          >
                            <DetalleModerno ficha={f} />
                          </PanelDeDetalle>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </>
        )}
      </table>
    </div>
  );
}
